/**
 * Surface Area generator (6.MGSR.1.3).
 *
 * Surface area of a rectangular prism, a cube, and (at harder levels) a right
 * triangular prism whose cross section is a Pythagorean right triangle so the
 * slant/hypotenuse is a whole number. All areas are summed with the geometry
 * helpers, so display and answer always agree. Dimensions are whole numbers.
 *
 * Families:
 *   F1: rectangular prism   (surfaceAreaRectPrism)
 *   F2: cube                (6 s^2)
 *   F3: triangular prism    (surfaceAreaTriPrism, right-triangle cross section)
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import {
  surfaceAreaRectPrism,
  surfaceAreaTriPrism,
  areaTriangle,
} from "../engine/geometry";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "surface-area" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3";

/** Right-triangle leg/leg/hypotenuse triples for clean triangular prisms. */
const RIGHT_TRIPLES: readonly [number, number, number][] = [
  [3, 4, 5],
  [6, 8, 10],
  [5, 12, 13],
  [8, 15, 17],
  [9, 12, 15],
];

interface Cfg {
  readonly families: readonly Family[];
  readonly max: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F2"], max: 8 },
  standard: { families: ["F1", "F2"], max: 12 },
  hard: { families: ["F1", "F2", "F3"], max: 15 },
  challenge: { families: ["F1", "F3"], max: 20 },
};

export class SurfaceAreaGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);

      let prompt: string;
      let latex: string;
      let answer: Rational;
      let stepDesc: string;

      if (family === "F1") {
        const l = rng.int(2, cfg.max);
        const w = rng.int(2, cfg.max);
        const h = rng.int(2, cfg.max);
        answer = surfaceAreaRectPrism(rat(l), rat(w), rat(h));
        prompt = `Find the surface area of a rectangular prism with length ${l}, width ${w}, and height ${h}.`;
        latex =
          `SA = 2(lw + lh + wh) = 2(${l}\\cdot${w} + ${l}\\cdot${h} + ${w}\\cdot${h}) = ${answer.toLatex()}`;
        stepDesc = "Add the areas of the three distinct face pairs, then double the total.";
      } else if (family === "F2") {
        const s = rng.int(2, cfg.max);
        answer = surfaceAreaRectPrism(rat(s), rat(s), rat(s));
        prompt = `Find the surface area of a cube with edge length ${s}.`;
        latex = `SA = 6 s^2 = 6 \\times ${s}^2 = ${answer.toLatex()}`;
        stepDesc = "A cube has 6 congruent square faces, so surface area is 6 times one face.";
      } else {
        // F3: right triangular prism with a Pythagorean cross section.
        const triple = rng.pick(RIGHT_TRIPLES);
        const leg1 = triple[0];
        const leg2 = triple[1];
        const hyp = triple[2];
        const length = rng.int(2, cfg.max);
        // Right triangle: legs are base and height.
        answer = surfaceAreaTriPrism(
          rat(leg1),
          rat(leg2),
          rat(leg1),
          rat(leg2),
          rat(hyp),
          rat(length),
        );
        const triArea = areaTriangle(rat(leg1), rat(leg2));
        prompt =
          `Find the surface area of a triangular prism of length ${length}. ` +
          `Its cross section is a right triangle with legs ${leg1} and ${leg2} and hypotenuse ${hyp}.`;
        latex =
          `SA = 2\\cdot\\tfrac{1}{2}(${leg1})(${leg2}) + (${leg1}+${leg2}+${hyp})\\times ${length} = ` +
          `${triArea.mul(rat(2)).toLatex()} + ${answer.sub(triArea.mul(rat(2))).toLatex()} = ${answer.toLatex()}`;
        stepDesc =
          "Add the two triangular ends (2 x triangle area) to the three rectangular sides (perimeter x length).";
      }

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: { text: prompt },
        answerType: "number",
        answer: { kind: "number", value: answer },
        steps: [{ description: stepDesc, latex }],
        hints: [
          { level: 1, text: "Surface area is the total area of every face of the solid." },
          { level: 2, text: "Find each face's area from its own dimensions, then add them all." },
        ],
        subskills: topic.subskills,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "number" ? q.answer.value.key() : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const surfaceArea = new SurfaceAreaGenerator();
